import { useState, useEffect } from 'react'
import { useNavigate, Navigate, useNavigationType } from 'react-router-dom'
import { Check, ChevronRight, Share2, Stethoscope } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useI18n } from '../../i18n'
import { ChatKeyframes, CompactHeader, OrellaChatBubble } from '../../components/OrellaChatHeader'
import { formatSpecialtyName } from '../../utils/specialtyResolver'
import ShareDoctorPanel from '../../components/share/ShareDoctorPanel'
import { loadShareDoctorPanel } from '../../utils/shareDoctorPanel'
import { matchDoctorsToCase, getMatchingSpecialties } from '../../utils/doctorMatch'
import { skipsDetect } from '../../utils/withdrawCase'
import { caseBack, previousPath } from '../../utils/caseBack' // PROD-654: back = the previous case screen

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'

// v206.1: SPECIALTY_KEY_MAP + formatSpecialtyName moved to shared utils/specialtyResolver.js

export default function ShareChoiceScreen() {
  const navigate = useNavigate()
  const { currentCase, updateCase, updateAndSaveCase } = useCaseStore()
  const { t } = useI18n()
  const [tapped, setTapped] = useState(null)
  const [showContent, setShowContent] = useState(() => {
    return !!sessionStorage.getItem(`orella_chat_share-choice-${currentCase?.id}`)
  })
  // P11 step 5 (TEST 2.19): the Orella card's doctor panel, the one DiagnoseScreen shows.
  const [showOrellaPanel, setShowOrellaPanel] = useState(false)
  const [orellaPanel, setOrellaPanel] = useState({ loading: false, yourDoctors: [], orellaDoctors: [] })

  useEffect(() => { window.scrollTo(0, 0) }, [])

  // v211.7: Referral-spawned cases auto-route based on delivery mode
  // Orella referral: doctor_id is already set → skip to share flow with MD pre-selected
  // External referral: no doctor_id → go to external path
  // ★★ PROD-654: this screen forwards a case that has already picked its side. Reached by a BACK
  //   (POP) from the next screen, forwarding would bounce the patient straight back there; with
  //   an in-app entry below this one, back continues past the fork instead. A direct entry (or a
  //   push) forwards as before, replacing this screen.
  const navType = useNavigationType()
  const forwardFromFork = (to) => {
    if (navType === 'POP' && previousPath() !== null) navigate(-1)
    else navigate(to, { replace: true })
  }

  useEffect(() => {
    if (currentCase?.originatorType === 'md_referral' && !currentCase?.doctorSource) {
      const hasOrellaDoctor = !!currentCase.sentToDoctor || !!currentCase.doctor
      if (hasOrellaDoctor) {
        console.log('[ShareChoice] Referral case with Orella MD — auto-routing to share')
        updateAndSaveCase({ doctorSource: 'orella', doctor_source: 'orella' }).catch(() => {
          updateCase({ doctorSource: 'orella' })
        })
        forwardFromFork('/case/share')
      }
    }
  }, [currentCase?.originatorType, currentCase?.doctorSource])

  // ★★★ TEST 1.07 — THE GUARD IS RE-DERIVED, because the handler that arrives here had
  //   deliberately falsified two of its three conditions. It read
  //       doctorSource === 'orella' && shareCode && (status === 'shared' || 'claimed')
  //   and DiagnoseScreen's handleCancelToOrella revokes the share code and sets the status
  //   to 'ready_to_share' immediately before navigating — so the one arm that would have
  //   carried an Orella case onward could never fire, and the patient fell through to a
  //   screen whose only outcome stamps the case EXTERNAL.
  //   The source of truth is doctorSource alone: this screen is the fork, and a case that
  //   has already taken the Orella side has nothing to choose here. shareCode and status
  //   are states of the Orella flow, not preconditions for entering it.
  useEffect(() => {
    if (currentCase?.doctorSource === 'orella') {
      forwardFromFork('/case/share')
    }
    if (currentCase?.doctorSource === 'external' && currentCase.status === 'submitted') {
      forwardFromFork('/case/detect')
    }
  }, [currentCase?.doctorSource, currentCase?.status])

  // DELETE-CURRENTCASE-NULL-1 — DECLARATIVE REDIRECT, NOT navigate() IN RENDER.
  // Calling navigate() here updates the router WHILE this component is rendering, and
  // React logs exactly that: "Cannot update a component (BrowserRouter) while rendering
  // a different component". That is the React error a patient saw when they deleted a
  // case mid-describe — deleteCase() nulls currentCase, this screen re-rendered on the
  // null and took this arm. The delete and the redirect both completed, which is why it
  // read as cosmetic. <Navigate> is the supported way to say the same thing.
  // ★★ P11 step 5 — BELOW EVERY HOOK. It sat above the two effects, so a render with a
  //   null case called two fewer hooks than the render before it (the rules of hooks).
  //   Both effects read currentCase through ?. and tolerate null.
  //   scripts/check-share-entry-fork.mjs SEF-4 holds it.
  if (!currentCase) return <Navigate to="/home" replace />

  // ★★★ P11 step 5 (TEST 2.19, Alex 2026-09-24) — PATH A: "Send to a doctor on orella".
  //   Opening the panel only READS (loadShareDoctorPanel), and a pick only navigates to that
  //   doctor's slot picker. No share code is created and nothing is written before a
  //   successful send; ShareScreen's handleSendToDoctor stamps doctor_source 'orella' then.
  //   A patient who backs out without sending lands on this fork again, unchanged.
  const openOrellaPanel = async () => {
    setShowOrellaPanel(true)
    setOrellaPanel({ loading: true, yourDoctors: [], orellaDoctors: [] })
    try {
      const { casesAPI, doctorsAPI, favoritesAPI } = await import('../../services/api')
      const { yourDoctors, orellaDoctors } = await loadShareDoctorPanel({ casesAPI, doctorsAPI, favoritesAPI, matchDoctorsToCase, getMatchingSpecialties, currentCase })
      setOrellaPanel({ loading: false, yourDoctors, orellaDoctors })
    } catch (err) {
      console.error('[ShareChoice] Orella doctor panel load failed:', err)
      setOrellaPanel({ loading: false, yourDoctors: [], orellaDoctors: [] })
    }
  }

  const handleOrellaPick = (doctor) => {
    setShowOrellaPanel(false)
    navigate('/case/share', { state: { selectedDoctor: doctor } })
  }

  const handleSelect = async (path) => {
    setTapped(path)
    
    // Set doctor_source on the case
    try {
      await updateAndSaveCase({ doctorSource: path, doctor_source: path })
      console.log(`[ShareChoice] Set doctor_source to '${path}'`)
    } catch (err) {
      console.error('[ShareChoice] Failed to save doctor_source:', err)
      // Still continue — local store has the value
      updateCase({ doctorSource: path })
    }

    // ★★★ TEST 1.07 — THIS BLOCK IGNORED ITS OWN ARGUMENT AND HARD-CODED 'external'.
    //   Its comment said "ONE OUTCOME … Ruling #5 puts the Orella route on the ready
    //   screen, so it does not return here." The shipped build contradicted that: the ready
    //   screen's RECOMMENDED control navigated straight here, and this block then stamped
    //   doctor_source='external' + status='submitted' on the server over a patient who had
    //   just asked for an Orella physician. The console said so in two lines.
    //   DiagnoseScreen now routes the Orella choice to /case/share, so the assumption holds
    //   again — but an assumption is not a guarantee, and this is where it was lost. The
    //   path argument is honoured, so no future caller can be answered with its opposite.
    setTimeout(async () => {
      // PROD-654: a push, not a replace (the fork stays in the history; back steps over it).
      if (path === 'orella') {
        navigate('/case/share')
        return
      }
      try {
        await updateAndSaveCase({ status: 'submitted' })
        const { casesAPI } = await import('../../services/api')
        await casesAPI.update(currentCase.id, { doctor_source: path, status: 'submitted' })
        console.log('[ShareChoice] External path persisted to API')
      } catch (err) {
        console.warn('[ShareChoice] API sync failed:', err)
        updateCase({ status: 'submitted' })
      }
      // ★ TEST 3.17 (patient push 3a, Q82): a case that comes back here after a withdrawal (or
      //   any case whose Detect is done with photos on it) goes on to the code step, not to the
      //   photo step that would ask for them again (utils/withdrawCase.js skipsDetect).
      navigate(skipsDetect(currentCase) ? '/case/diagnose' : '/case/detect') // PROD-654: a push
    }, 350)
  }

  const caseInfo = `${formatSpecialtyName(currentCase.specialtyName || currentCase.specialty, t)} › ${currentCase.focusAreaName || currentCase.focusArea || ''}`

  // ★★★ P11 step 5 (TEST 2.19) — THE FORK IS BACK, by Alex's decision of 2026-09-24. It
  //   supersedes share-remodel ruling #5 (Wave 2, a9409412: the Orella route only on the
  //   ready screen); the superseding record is in the decisions log. Card A (Orella doctor)
  //   comes first (P11 D7); card B below is today's code card, unchanged.
  //
  // v242.75.0 (SHARE-CODE-FIRST-1) — the two-card fork is gone.
  //
  // It offered "Share with an Orella Doctor" as RECOMMENDED and "Prepare for My
  // Own Doctor" second, which inverted what the product actually does best: the
  // code path works for ANY doctor, needs nobody to be on the platform, and the
  // whole five-step chain behind it — Detect, Visit Prep, generating, agreement,
  // code — was already built and shipped. The screen was steering patients away
  // from the finished path toward the one that depends on a matching doctor
  // existing. Code first, doctors second, and the doctors are now REAL rows
  // rather than a description of a category.

  return (
    <div className="min-h-full bg-gray-50 flex flex-col">
      <ChatKeyframes />
      <CompactHeader
        stepNum={2}
        totalSteps={3}
        stepLabel={t('shareChoice.stepLabel') || 'Share'}
        caseInfo={caseInfo}
        onBack={() => caseBack(navigate)}
        onNotification={() => navigate('/notifications')}
      />
      <OrellaChatBubble
        message={t('shareChoice.chatMessage', "Let's build a report of your case for your doctor.")}
        animationKey={`share-choice-${currentCase?.id}`}
        onComplete={() => {
          setShowContent(true)
          try { sessionStorage.setItem(`orella_chat_share-choice-${currentCase?.id}`, '1') } catch (e) {}
        }}
      />

      <div
        className="flex-1 px-4 pt-1 pb-6"
        style={{
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          transitionDelay: '150ms',
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(8px)',
        }}
      >
        {/* ── A: send to a doctor on orella (P11 step 5, TEST 2.19) ── */}
        <button
          onClick={openOrellaPanel}
          disabled={!!tapped}
          className="flex items-center gap-3 w-full text-left rounded-[18px] p-3.5 mb-3 active:scale-[0.99] transition-transform disabled:opacity-60"
          style={{ border: `1.5px solid ${BLUE}`, background: '#F3F9FF' }}
        >
          <span className="w-[42px] h-[42px] rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: BLUE }}>
            <Stethoscope size={20} className="text-white" />
          </span>
          <span className="min-w-0">
            <span className="block text-[14.5px] font-semibold leading-tight" style={{ color: NAVY }}>{t('diagnose.sendToOrellaDoc')}</span>
            <span className="block text-[11.5px] mt-1 leading-snug" style={{ color: '#5A6B7B' }}>{t('diagnose.pickBoardCertified')}</span>
          </span>
          <ChevronRight size={18} className="ml-auto flex-shrink-0" style={{ color: '#94A3B8' }} />
        </button>

        {/* ── RECOMMENDED: the code path ── */}
        {/* v242.77.0 (TF-29) — NAVY, not blue. Mockup v5: `.lead` is
            `1.5px solid var(--navy)`, `.rec` is a navy corner tab anchored
            top-RIGHT with a 10px bottom-left radius, and `.big` is a navy
            fill. I built all three in ORELLA_BLUE. Blue is the app's
            interactive colour and it is everywhere; navy is what makes this
            one card read as the recommendation rather than as another
            actionable tile. */}
        <div style={{
          background: '#fff', border: `1.5px solid ${NAVY}`, borderRadius: 18,
          padding: 15, position: 'relative', overflow: 'hidden',
        }}>
          <div className="flex items-center gap-2.5" style={{ marginBottom: 6, marginTop: 2 }}>
            <span style={{
              width: 30, height: 30, borderRadius: 9, flex: '0 0 30px', background: ICE_BLUE,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Share2 size={15} style={{ color: BLUE }} strokeWidth={2.2} />
            </span>
            <span style={{ fontWeight: 800, fontSize: 15, color: NAVY, lineHeight: 1.2 }}>
              {t('shareChoice.anyDoctorTitle', 'Build your Case Report')}
            </span>
          </div>

          <p style={{ fontSize: 12.5, lineHeight: 1.5, color: '#475569', margin: '0 0 10px' }}>
            {t('shareChoice.anyDoctorDesc', 'Add your photos, then Orella builds a Case Report with a share code. Any doctor can open your full case to prepare for your consultation.')}
          </p>

          <div style={{ marginBottom: 12 }}>
            {[
              t('shareChoice.anyBenefit1', 'Health profile, symptoms, photos & scans'),
              t('shareChoice.anyBenefit2', 'Share by code, QR, wallet pass or PDF'),
              t('shareChoice.anyBenefit3', 'Upload their notes after — Orella organizes it'),
            ].map((b) => (
              <div key={b} className="flex items-start" style={{ gap: 8, marginBottom: 6 }}>
                <span className="flex items-center justify-center flex-none"
                      style={{ width: 16, height: 16, borderRadius: '50%', background: 'rgba(70,168,249,0.13)', marginTop: 1 }}>
                  <Check size={9} style={{ color: BLUE }} strokeWidth={3} />
                </span>
                <span style={{ fontSize: 11.8, color: '#64748B', lineHeight: 1.45 }}>{b}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => !tapped && handleSelect('external')}
            disabled={!!tapped}
            className={`w-full flex items-center justify-center gap-2 active:scale-[0.98] transition-transform ${tapped ? '' : 'breathe-navy'}`}
            style={{
              background: NAVY, color: '#fff', border: 'none', borderRadius: 999,
              padding: '13px 16px', fontWeight: 800, fontSize: 14,
              opacity: tapped === 'external' ? 0.75 : 1,
              boxShadow: '0 4px 14px rgba(21,53,79,0.28)',
            }}
          >
            {tapped === 'external'
              ? (t('shareChoice.opening') || 'Opening…')
              : t('shareChoice.generateCode', 'Generate my share code')}
            <ChevronRight size={16} />
          </button>
        </div>

      </div>

      {showOrellaPanel && (
        <ShareDoctorPanel
          loading={orellaPanel.loading}
          yourDoctors={orellaPanel.yourDoctors}
          orellaDoctors={orellaPanel.orellaDoctors}
          onPick={handleOrellaPick}
          onClose={() => setShowOrellaPanel(false)}
          closeLabel={t('common.close')}
        />
      )}
    </div>
  )
}
